import useEmblaCarousel from 'embla-carousel-react'
import { cn } from 'oddle-shop-monorepo.design-system'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import {
  NextButton,
  PrevButton,
  usePrevNextButtons,
} from './SlideshowArrowButtons'
import {
  DotButton,
  useDotButton,
} from '@/components/sections/SlideshowBanner/SlideshowDots'
import SlideOverlay from '@/components/sections/SlideshowBanner/SlideOverlay'
import { useSelector } from 'react-redux'
import { selectDetailMenuSite } from '@/redux/meta'
import { selectPreviewMode } from '@/redux/customisation'
import { defaultImageSource } from '@/libs/config/client'
import useResponsive from '@/hooks/useResponsive'
import ImageWithLoading from '@/components/common/ImageWithLoading'

interface SlideshowCarouselProps {
  autoplay?: boolean
  autoplayInterval?: number
  loop?: boolean
  className?: string
  images: any[]
}

const SlideshowCarousel: FC<SlideshowCarouselProps> = ({
  autoplay = false,
  autoplayInterval = 5000,
  loop = true,
  className,
  images,
}) => {
  const previewMode = useSelector(selectPreviewMode)
  const menuSite = useSelector(selectDetailMenuSite)
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop,
    containScroll: 'trimSnaps',
  })
  const { mobile: isMobile } = useResponsive()

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi)

  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi)

  const [autoplayActive, setAutoplayActive] = useState(false)
  const [isFullyInView, setIsFullyInView] = useState(false)
  const carouselRef = useRef<HTMLDivElement>(null)

  // Viewport detection for autoplay control
  useEffect(() => {
    if (!carouselRef.current || !autoplay) return

    const carousel = carouselRef.current
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry) {
          // Only enable autoplay when slideshow is fully visible (100% in viewport)
          const fullyVisible = entry.intersectionRatio >= 0.95
          setIsFullyInView(fullyVisible)
          setAutoplayActive(fullyVisible)
        }
      },
      {
        threshold: [0, 0.5, 0.95, 1.0], // Track different visibility levels
        rootMargin: '0px', // No margin - strict viewport detection
      }
    )

    observer.observe(carousel)
    return () => observer.disconnect()
  }, [autoplay])

  // Autoplay functionality - only when fully in view
  useEffect(() => {
    if (!emblaApi || !autoplayActive || !isFullyInView) return

    const interval = setInterval(() => {
      if (emblaApi.canScrollNext()) {
        emblaApi.scrollNext()
      } else {
        emblaApi.scrollTo(0)
      }
    }, autoplayInterval)

    return () => clearInterval(interval)
  }, [emblaApi, autoplayActive, isFullyInView, autoplayInterval])

  // Pause autoplay on hover - but only if already active
  const handleMouseEnter = useCallback(() => {
    if (autoplay && isFullyInView) {
      setAutoplayActive(false)
    }
  }, [autoplay, isFullyInView])

  const handleMouseLeave = useCallback(() => {
    if (autoplay && isFullyInView) {
      setAutoplayActive(true)
    }
  }, [autoplay, isFullyInView])

  const menuName = menuSite?.menu?.name || ''

  return (
    <div
      ref={carouselRef}
      className={cn('relative', className)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {images.map(({ src, ...overlayTextSettings }, index) => (
            <div
              key={index}
              className={cn(
                'h-fit flex-[0_0_100%]',
                index === selectedIndex || images.length === 1
                  ? 'h-auto'
                  : 'h-[1px]'
              )}
            >
              <div className="relative flex h-fit flex-col rounded-2xl">
                <div className="relative aspect-[12/5] w-full rounded-2xl">
                  <ImageWithLoading
                    src={src || defaultImageSource}
                    alt={menuName || 'Slideshow image'}
                    fill
                    className="rounded-none bg-[var(--color-light-gray)] object-cover md:rounded-2xl"
                    blurImageClassName="rounded-none md:rounded-2xl"
                    priority={index === 0}
                    loading="eager"
                  />
                  {images.length > 1 && (
                    <div className="absolute bottom-6 z-[2] flex w-full flex-wrap items-center justify-center gap-3">
                      {scrollSnaps.map((_, indexScrollSnaps) => (
                        <DotButton
                          key={indexScrollSnaps}
                          onClick={() => onDotButtonClick(indexScrollSnaps)}
                          className={cn(
                            'bg-button m-0 flex h-3 w-3 cursor-pointer touch-manipulation appearance-none items-center justify-center rounded-full border-0 p-0 md:hidden',
                            indexScrollSnaps === selectedIndex
                              ? 'opacity-80'
                              : 'opacity-20'
                          )}
                        />
                      ))}
                    </div>
                  )}
                </div>
                <SlideOverlay
                  {...overlayTextSettings}
                  previewMode={previewMode}
                  active={index === selectedIndex || images.length === 1}
                  className="hidden md:z-[3] md:flex"
                />
                <SlideOverlay
                  {...overlayTextSettings}
                  previewMode={previewMode}
                  active
                  className="flex h-fit md:hidden"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
      {images.length > 1 && (
        <>
          <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} />
          <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} />
          <div className="absolute bottom-6 hidden w-full flex-wrap items-center justify-center gap-3 md:flex">
            {scrollSnaps.map((_, index) => (
              <DotButton
                key={index}
                onClick={() => onDotButtonClick(index)}
                className={cn(
                  'm-0 flex h-3 w-3 cursor-pointer touch-manipulation appearance-none items-center justify-center rounded-full border-0 p-0',
                  index === selectedIndex ? 'opacity-80' : 'opacity-20',
                  isMobile ? 'bg-button' : 'bg-header-background-color'
                )}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export default SlideshowCarousel
